// `extension-alert-candidates-export.api.ng1.ts` — pushing candidates into an ATS list.
//
// ⚠️ THIS WRITES INTO A CUSTOMER'S ATS. Nothing should call it except a user pressing Export.
import { fetchFile, type DownloadedFile } from './download.ts'
import { request } from './request.ts'

/**
 * `CandidateExportAsyncRequest` — a UNION, and the union is the contract.
 *
 * ⚠️ EITHER AN EXISTING LIST OR A NEW ONE, NEVER BOTH AND NEVER NEITHER. Upstream types it as
 * `({ listId } | { name, description, isPublic }) & { candidateIds }`, so sending `listId`
 * alongside a `name` is not "a list with a name" — it is two different writes described at
 * once, and which one the server performs is its business rather than the caller's. Modelled
 * as a discriminated union here so the compiler refuses the ambiguous shape.
 */
export type ExportListTarget =
  | { listId: string; name?: never; description?: never; isPublic?: never }
  | { listId?: never; name: string; description: string; isPublic: boolean }

export type CandidateExportRequest = { candidateIds: number[] } & ExportListTarget

/**
 * `ExportClientContactsToAtsListAsyncRequest` — the same union over a different id field.
 *
 * ⚠️ `clientContactIds`, AND A DIFFERENT START URL. The contacts export posts to
 * `exportClientContactsToAtsListAsync`, but it polls the SAME
 * `exportToAtsListAsyncStatus` as candidates do — the handle is a message id, not an entity
 * reference, so there is one status endpoint for both products
 * (`extension-alert-client-contacts-export.api.ng1.ts:26,37`).
 */
export type ClientContactExportRequest = { clientContactIds: number[] } & ExportListTarget

export interface ExportStartedResponse {
  exportMessageId: number
}

export interface ExportStatusResponse {
  success: boolean
  outputParamsJson?: unknown
}

/**
 * `exportToAtsListAsync` — starts the export and returns a handle, it does not wait.
 *
 * ⚠️ IT ANSWERS WITH AN ID, NOT AN OUTCOME. The write is still in flight when this resolves;
 * `exportStatus` is how the result is learned. Treating the resolution as success reports a
 * completed export for one that may yet fail in the ATS.
 */
export async function exportToAtsList(request_: CandidateExportRequest): Promise<ExportStartedResponse> {
  return request('POST', '/chrome-extension-api/ats-export-list/exportToAtsListAsync', request_)
}

/** The contacts twin. Same contract, same caveat — an id, not an outcome. */
export async function exportClientContactsToAtsList(
  request_: ClientContactExportRequest,
): Promise<ExportStartedResponse> {
  return request('POST', '/chrome-extension-api/ats-export-list/exportClientContactsToAtsListAsync', request_)
}

/** `exportToAtsListAsyncStatus` — poll the handle either start call returned. */
export async function exportStatus(exportMessageId: number): Promise<ExportStatusResponse> {
  return request('POST', '/chrome-extension-api/ats-export-list/exportToAtsListAsyncStatus', { exportMessageId })
}

/**
 * `IAlertCandidateCsvExportParams` — what "Export to file" posts
 * (`extension-alert-candidates.component.ng1.ts:200`).
 *
 * ⚠️ THE IDS ARE NESTED, under `alertIdCandidateIdsParams`, and the two strings beside them are
 * the server's raw material for the download's NAME: `dateText` is the client's local
 * `yyyy-MM-dd` (`utils.getNowStringYYYYMMDDWithSeparator('-')`), not a filter.
 */
export interface CandidateCsvExportRequest {
  alertName: string
  dateText: string
  alertIdCandidateIdsParams: { candidateIds: number[]; alertId: number }
}

/**
 * `exportCandidatesToCsv` — upstream's `ShortlistHunterCandidateActionsService.exportCandidatesToCsv`
 * is `fileDownload.saveFile(url, null, 'POST', params)`: a JSON body, an arraybuffer answer, and
 * the name from `Content-Disposition`. `fetchFile` is that helper's fetch half; `packages/ui`'s
 * `saveFile` writes it.
 *
 * ⚠️ IT IS DEFAULT-ON AND THE SERVER STILL REFUSES IT WHEN THE TENANT SAYS SO. The action is drawn
 * unless `featureFlags.EXPORT_TO_FILE_DISABLED`, and with that flag set this endpoint answers 403
 * (measured on the dev tenant, 28 Oct). The rejection is the caller's to report.
 *
 * Upstream's fallback name is `null + '.csv'` — it passes no fallback — and is reached only when
 * the server sends no usable `Content-Disposition`. This names the file after the hunter and the
 * date instead, which is what the request hands the server to compose its own name from.
 *
 * There is NO contacts twin: `onExportClientContactsToCsvClicked` is commented out upstream
 * (`extension-alert-client-contacts.component.ng1.ts:417,2077`).
 */
export async function exportCandidatesToCsv(
  alertId: number,
  alertName: string,
  candidateIds: number[],
  dateText: string,
): Promise<DownloadedFile> {
  const body: CandidateCsvExportRequest = {
    alertName,
    dateText,
    alertIdCandidateIdsParams: { candidateIds, alertId },
  }
  return fetchFile('/chrome-extension-api/alert-candidate/exportCandidatesToCsv', {
    method: 'POST',
    body,
    fallbackFilename: `${alertName} ${dateText}.csv`,
    failureMessage: 'The export failed.',
  })
}
